import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Full-screen image viewer.
 *
 * The same markup had been copy-pasted into every surface that shows a
 * photo thumbnail, so the close affordances drifted between them. This
 * is the shared one: click the backdrop or the button to dismiss, and
 * Escape works too, which none of the copies handled.
 *
 * **Rendered through a portal to `document.body`.** `Dialog` does not
 * portal: it renders `fixed inset-0 z-50` inline, and its panel is
 * `overflow-hidden`. A lightbox opened from inside a dialog was
 * therefore trapped in that stacking context, where no `z-index` can
 * lift it above the dialog's own content, and clipped by the panel on
 * top of that. Opened from the polygon map it appeared *under* the map.
 * Escaping to the body root is the only fix that does not depend on
 * out-guessing every ancestor's z-index.
 *
 * Why the number is so large: `.leaflet-container` sets no z-index, so
 * it creates no stacking context, and Leaflet's own panes (tiles 200,
 * overlay 400, markers 600, popups 700, controls 800, corners 1000) sit
 * in whatever context contains the map. A lightbox in that same context
 * at a smaller value is painted over by the map itself, which is
 * precisely how it ended up underneath. The portal already settles it;
 * the value stays clear of Leaflet's range as belt and braces.
 */
export function ImageLightbox({
  url,
  alt = '',
  caption,
  onClose,
}: {
  url: string | null;
  alt?: string;
  /** Shown under the image, on one line. Usually the file name. */
  caption?: string;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!url) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Claim the key so a surrounding dialog does not also close on the
      // same press: the lightbox is the innermost layer.
      e.stopPropagation();
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [url, onClose]);

  if (!url) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      role="presentation"
    >
      <button
        type="button"
        className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/40"
        // The button sits inside the dismissing backdrop, so without this
        // a single click fires `onClose` twice.
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="close"
      >
        <X className="h-6 w-6" />
      </button>
      <figure
        className="flex max-h-full max-w-full flex-col items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={url}
          alt={alt}
          referrerPolicy="no-referrer"
          className="max-h-[82vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
        />
        {caption ? (
          /* One line, left to right. Letting it wrap inside a container
             only as wide as the image made a long file name break a
             character at a time and read vertically. */
          <figcaption className="max-w-[90vw] overflow-x-auto whitespace-nowrap rounded bg-white/10 px-3 py-1 text-center text-sm text-white">
            {caption}
          </figcaption>
        ) : null}
      </figure>
    </div>,
    document.body,
  );
}
